import { type ChangeEvent, type DragEvent, useRef, useState } from 'react';
import { Upload, Search, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DocumentTypeMultiSelect } from '@/components/portfolio/transactions/DocumentTypeMultiSelect';
import {
  useEntityFolder,
  useSearchDocuments,
  useUploadDocument,
} from '@/hooks/react-query/useDocuments';
import {
  type DocumentSource,
  type DocumentType,
  MAX_FILE_SIZE,
  SUPPORTED_FILE_TYPES,
  type Document,
} from '@/types/document';
import { useSafeToast } from '@/hooks/useSafeToast';
import { formatDate, formatFileSize } from '@/utils/formatters';

interface AttachDocumentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityType: 'PORTFOLIO_COMPANY' | 'FUND';
  entityId: string;
  acceptedTags: Set<DocumentType>;
  documentSource: DocumentSource;
  /** Guidance under the type picker. */
  documentTypesHint: string;
  /** Shown when a picked type is outside acceptedTags. */
  contextOnlyTagHint: string;
  /** Search this folder only; omit to search every document. */
  searchCollectionId?: string;
  onAttach: (
    documentId: string,
    documentTypes: DocumentType[]
  ) => Promise<void>;
}

export function AttachDocumentDialog({
  open,
  onOpenChange,
  entityType,
  entityId,
  acceptedTags,
  documentSource,
  documentTypesHint,
  contextOnlyTagHint,
  searchCollectionId,
  onAttach,
}: AttachDocumentDialogProps) {
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [tab, setTab] = useState<'upload' | 'pick'>('upload');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { toast } = useSafeToast();

  const { data: folderInfo } = useEntityFolder(entityType, entityId);
  const uploadDocument = useUploadDocument();

  const hasContextOnlyTag = documentTypes.some(
    (type) => !acceptedTags.has(type)
  );

  const searchEnabled = tab === 'pick' && searchQuery.length >= 2;
  const { data: searchResults, isLoading: isSearching } = useSearchDocuments(
    {
      query: searchQuery,
      limit: 20,
      ...(searchCollectionId ? { collection_id: searchCollectionId } : {}),
    },
    { enabled: searchEnabled }
  );

  const tagSelected = documentTypes.length > 0;

  const handleClose = () => {
    if (isSubmitting) return;
    setDocumentTypes([]);
    setSearchQuery('');
    setTab('upload');
    setDragActive(false);
    onOpenChange(false);
  };

  const handleUploadFile = async (file: File) => {
    if (!tagSelected || !folderInfo?.folder_id) return;

    if (file.size > MAX_FILE_SIZE) {
      toast({
        title: 'File too large',
        description: `${file.name} is ${formatFileSize(file.size)}. Maximum is ${formatFileSize(MAX_FILE_SIZE)}.`,
        variant: 'destructive',
      });
      return;
    }

    // Some drag-and-drop sources provide an empty MIME type for valid files.
    // In that case we allow upload and rely on backend validation.
    if (file.type && !SUPPORTED_FILE_TYPES.includes(file.type)) {
      toast({
        title: 'Unsupported file type',
        description: `${file.name} (${file.type || 'unknown'}) is not a supported type.`,
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const uploadResult = await uploadDocument.mutateAsync({
        file,
        collection_id: folderInfo.folder_id,
        document_source: documentSource,
        document_types: documentTypes,
      });
      const uploadedId = uploadResult.document?.id;
      if (!uploadedId) return;

      await onAttach(uploadedId, documentTypes);
      handleClose();
    } catch (error) {
      const fallbackMessage = 'Failed to upload and attach document';
      const message =
        (
          error as {
            response?: { data?: { detail?: string } };
            message?: string;
          }
        )?.response?.data?.detail ||
        (error as { message?: string })?.message ||
        fallbackMessage;

      toast({
        title: 'Upload failed',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpload = () => {
    if (!tagSelected || !folderInfo?.folder_id) return;
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    await handleUploadFile(files[0]);
    event.target.value = '';
  };

  const handleDrop = async (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    if (!tagSelected || isSubmitting || !folderInfo?.folder_id) return;

    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    await handleUploadFile(file);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isSubmitting) {
      setDragActive(true);
    }
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
  };

  const handlePick = async (doc: Document) => {
    if (!tagSelected) return;
    setIsSubmitting(true);
    try {
      await onAttach(doc.id, documentTypes);
      handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const resultItems = searchResults?.documents ?? [];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[90dvh] max-w-xl grid-rows-[auto_minmax(0,1fr)_auto]">
        <DialogHeader>
          <DialogTitle>Attach document</DialogTitle>
          <DialogDescription>
            Attach a document to this transaction. The type tag becomes one of
            the verification sources.
          </DialogDescription>
        </DialogHeader>

        <div className="scrollbar-always space-y-4 overflow-y-auto px-1">
          <div className="space-y-2">
            <Label htmlFor="link-document-type">Document types</Label>
            <DocumentTypeMultiSelect
              id="link-document-type"
              value={documentTypes}
              onChange={setDocumentTypes}
              acceptedTags={acceptedTags}
            />
            <p className="text-xs text-muted-foreground">{documentTypesHint}</p>
            {hasContextOnlyTag && (
              <p className="text-xs text-amber-600">{contextOnlyTagHint}</p>
            )}
          </div>

          <Tabs
            value={tab}
            onValueChange={(v) => setTab(v as 'upload' | 'pick')}
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="upload">Upload new</TabsTrigger>
              <TabsTrigger value="pick">Pick existing</TabsTrigger>
            </TabsList>

            <TabsContent value="upload" className="pt-3">
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  dragActive ? 'border-primary bg-primary/5' : 'border-gray-300'
                }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept={SUPPORTED_FILE_TYPES.join(',')}
                  onChange={handleFileSelect}
                  data-testid="attach-document-file-input"
                />
                <Upload className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-gray-600 mb-3">
                  Drag and drop a file here, or select one to upload and link it
                  to this transaction
                </p>
                <Button
                  variant="outline"
                  onClick={handleUpload}
                  disabled={
                    !tagSelected || isSubmitting || !folderInfo?.folder_id
                  }
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    'Select file'
                  )}
                </Button>
                {!tagSelected && (
                  <p className="mt-3 text-xs text-muted-foreground">
                    Select at least one document type first
                  </p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="pick" className="pt-3">
              <div className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    className="pl-9"
                    placeholder="Search existing documents by name…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>

                <div className="max-h-64 overflow-y-auto border rounded-md">
                  {!searchEnabled && (
                    <p className="p-4 text-sm text-muted-foreground text-center">
                      Type at least 2 characters to search
                    </p>
                  )}
                  {searchEnabled && isSearching && (
                    <p className="p-4 text-sm text-muted-foreground text-center">
                      Searching…
                    </p>
                  )}
                  {searchEnabled &&
                    !isSearching &&
                    resultItems.length === 0 && (
                      <p className="p-4 text-sm text-muted-foreground text-center">
                        No documents found
                      </p>
                    )}
                  {resultItems.map((doc) => (
                    <button
                      type="button"
                      key={doc.id}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b last:border-b-0 disabled:opacity-50"
                      onClick={() => handlePick(doc)}
                      disabled={!tagSelected || isSubmitting}
                    >
                      <div className="text-sm font-medium truncate">
                        {doc.name}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {doc.original_filename} · {formatDate(doc.upload_date)}
                      </div>
                    </button>
                  ))}
                </div>
                {!tagSelected && (
                  <p className="text-xs text-muted-foreground">
                    Select at least one document type first
                  </p>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
